import { AlertOutlined, SearchOutlined } from '@ant-design/icons';
import { Flex, Input, Select, Typography } from 'antd';
import { FilterBar } from '~/app/components/common/FilterBar/FilterBar';
import {
  FilterBarActions,
  FilterBarAddButton,
} from '~/app/components/common/FilterBar/FilterBarToolbar';
import type { VendorErrorCodeResponseDto } from '@evimo/client/models';
import { useTranslation } from 'react-i18next';
import { VendorErrorCodeDrawer } from '~/app/components/errorManagement/VendorErrorCodeDrawer/VendorErrorCodeDrawer';
import { VendorErrorCodeViewDrawer } from '~/app/components/errorManagement/VendorErrorCodeViewDrawer/VendorErrorCodeViewDrawer';
import { useErrorManagementColumns } from '~/app/components/errorManagement/ErrorManagementPage/hooks/useErrorManagementColumns';
import type {
  SelectOption,
  VendorErrorCodeDrawerMode,
  VendorErrorCodeFormValues,
} from '~/app/components/errorManagement/ErrorManagementPage/types';
import type { CreatedAtSortOrder } from '~/app/components/errorManagement/CreatedAtSortHeader/CreatedAtSortHeader';
import { toFormValues } from '~/app/components/errorManagement/ErrorManagementPage/utils';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';
import { EvimoListPanel } from '~/app/components/common/EvimoListPanel/EvimoListPanel';

export type ErrorManagementPageProps = {
  rows: VendorErrorCodeResponseDto[];
  total: number;
  page: number;
  pageSize: number;
  searchValue: string;
  manufacturerFilter: string | undefined;
  manufacturerOptions: SelectOption[];
  assetTypeOptions: SelectOption[];
  sortOrder: CreatedAtSortOrder;
  isLoading: boolean;
  isDrawerOpen: boolean;
  drawerMode: VendorErrorCodeDrawerMode;
  editingRecord: VendorErrorCodeResponseDto | null;
  isViewDrawerOpen: boolean;
  viewingRecord: VendorErrorCodeResponseDto | null;
  isSubmitting: boolean;
  onSearchChange: (value: string) => void;
  onManufacturerFilterChange: (value: string | undefined) => void;
  onPageChange: (page: number, pageSize: number) => void;
  onToggleSortOrder: () => void;
  onAdd: () => void;
  onView: (record: VendorErrorCodeResponseDto) => void;
  onEdit: (record: VendorErrorCodeResponseDto) => void;
  onDelete: (record: VendorErrorCodeResponseDto) => void;
  onReopen: (record: VendorErrorCodeResponseDto) => void;
  onDrawerClose: () => void;
  onViewDrawerClose: () => void;
  onDrawerSubmit: (values: VendorErrorCodeFormValues) => Promise<void>;
  hidePageHeader?: boolean;
};

const HEADER_ICON_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: 12,
  background: 'color-mix(in srgb, var(--evimo-blue) 12%, transparent)',
  color: 'var(--evimo-blue)',
  fontSize: 20,
} as const;

export function ErrorManagementPage({
  rows,
  total,
  page,
  pageSize,
  searchValue,
  manufacturerFilter,
  manufacturerOptions,
  assetTypeOptions,
  sortOrder,
  isLoading,
  isDrawerOpen,
  drawerMode,
  editingRecord,
  isViewDrawerOpen,
  viewingRecord,
  isSubmitting,
  onSearchChange,
  onManufacturerFilterChange,
  onPageChange,
  onToggleSortOrder,
  onAdd,
  onView,
  onEdit,
  onDelete,
  onReopen,
  onDrawerClose,
  onViewDrawerClose,
  onDrawerSubmit,
  hidePageHeader = false,
}: ErrorManagementPageProps) {
  const { t } = useTranslation('errorManagement');
  const columns = useErrorManagementColumns({
    sortOrder,
    onToggleSortOrder,
    onView,
    onEdit,
    onDelete,
    onReopen,
  });

  return (
    <Flex vertical gap={16}>
      {!hidePageHeader ? (
        <Flex gap={12} align="center">
          <span style={HEADER_ICON_STYLE} aria-hidden>
            <AlertOutlined />
          </span>
          <div>
            <Typography.Title
              level={3}
              className="ds-page-title"
              style={{ margin: 0, fontSize: 28 }}
            >
              {t('title')}
            </Typography.Title>
            <Typography.Paragraph
              type="secondary"
              style={{ margin: '4px 0 0' }}
            >
              {t('description')}
            </Typography.Paragraph>
          </div>
        </Flex>
      ) : null}

      <EvimoListPanel>
        <FilterBar>
          <Input
            allowClear
            className="evimo-filter-search"
            prefix={<SearchOutlined />}
            placeholder={t('searchPlaceholder')}
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t('manufacturerFilter')}
            value={manufacturerFilter}
            onChange={(value) => onManufacturerFilterChange(value)}
            options={manufacturerOptions}
          />
          <FilterBarActions>
            <FilterBarAddButton
              ns="errorManagement"
              labelKey="addErrorCode"
              defaultLabel="Add error code"
              onClick={onAdd}
            />
          </FilterBarActions>
        </FilterBar>

        <EvimoTable
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={rows}
          locale={{ emptyText: t('empty') }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: onPageChange,
          }}
        />
      </EvimoListPanel>

      <VendorErrorCodeDrawer
        open={isDrawerOpen}
        mode={drawerMode}
        initialValues={editingRecord ? toFormValues(editingRecord) : undefined}
        manufacturerOptions={manufacturerOptions}
        assetTypeOptions={assetTypeOptions}
        submitting={isSubmitting}
        onClose={onDrawerClose}
        onSubmit={onDrawerSubmit}
      />

      <VendorErrorCodeViewDrawer
        open={isViewDrawerOpen}
        record={viewingRecord}
        onClose={onViewDrawerClose}
      />
    </Flex>
  );
}
